import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTheme } from 'styled-components';
import { railIcons } from '../../NavLayout/config/navIcons';
import {
  DrawerSection,
  DrawerSectionHeader,
  DrawerExpandChevron,
  DrawerSubGroup,
  DrawerItem,
  DrawerItemExternal,
  DrawerItemButton,
  DrawerDot,
  DrawerExpandGroupTitle,
  MobileIcon,
} from '../styled/NavMobileLayout.styled';

const SectionIcon = ({ id }) => (
  <MobileIcon viewBox="0 0 24 24">{railIcons[id]}</MobileIcon>
);

const MobileMenuSection = ({
  section,
  isActive,
  onClose,
  onStripeClick,
  onPermissionDenied,
}) => {
  const { pathname } = useLocation();
  const theme = useTheme();
  const stripeEnabledColor =
    theme?.name === 'light' ? '#475569' : 'rgba(255, 255, 255, 0.65)';

  // Only expand if current route matches one of this section's panel items
  const routeMatchesSection = () => {
    if (!section.panelItems) return false;
    return section.panelItems.some((item) => {
      if (item.link && pathname === item.link) return true;
      if (item.link && pathname.startsWith(item.link)) return true;
      if (item.children) {
        return item.children.some(
          (child) => child.link && pathname.startsWith(child.link)
        );
      }
      return false;
    });
  };

  const [isExpanded, setIsExpanded] = useState(routeMatchesSection);
  const [expandedGroups, setExpandedGroups] = useState({});

  // Reset expanded state when route changes
  useEffect(() => {
    setIsExpanded(routeMatchesSection());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // Direct nav — single clickable item
  if (section.type === 'direct') {
    if (section.shown === false) {
      return (
        <DrawerSection>
          <DrawerSectionHeader isActive={false} onClick={onPermissionDenied}>
            <SectionIcon id={section.id} />
            {section.label}
          </DrawerSectionHeader>
        </DrawerSection>
      );
    }
    return (
      <DrawerSection>
        <DrawerSectionHeader
          isActive={isActive}
          onClick={() => {
            onClose(section.link);
          }}
        >
          <SectionIcon id={section.id} />
          {section.label}
        </DrawerSectionHeader>
      </DrawerSection>
    );
  }

  // Section with panel items — expandable group
  if (section.shown === false) {
    return (
      <DrawerSection>
        <DrawerSectionHeader isActive={false} onClick={onPermissionDenied}>
          <SectionIcon id={section.id} />
          {section.label}
        </DrawerSectionHeader>
      </DrawerSection>
    );
  }

  const hasItems = section.panelItems && section.panelItems.length > 0;

  const toggleExpandGroup = (label) => {
    setExpandedGroups((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  return (
    <DrawerSection>
      <DrawerSectionHeader
        isActive={isActive}
        onClick={() => {
          if (hasItems) {
            setIsExpanded(!isExpanded);
          } else {
            onClose(section.link);
          }
        }}
      >
        <SectionIcon id={section.id} />
        {section.label}
        {hasItems && (
          <DrawerExpandChevron isExpanded={isExpanded} viewBox="0 0 24 24">
            <polyline points="6 9 12 15 18 9" />
          </DrawerExpandChevron>
        )}
      </DrawerSectionHeader>

      {hasItems && (
        <DrawerSubGroup isExpanded={isExpanded}>
          {section.panelItems.map((item, idx) => {
            // Expandable sub-group
            if (item.type === 'expandable') {
              if (item.shown === false) return null;
              const groupExpanded = expandedGroups[item.label] !== false;
              return (
                <div key={item.label}>
                  <DrawerExpandGroupTitle
                    onClick={() => toggleExpandGroup(item.label)}
                    style={{ cursor: 'pointer' }}
                  >
                    {item.label} {groupExpanded ? '▴' : '▾'}
                  </DrawerExpandGroupTitle>
                  {groupExpanded &&
                    item.children.map((child) => (
                      <DrawerItem
                        key={child.link}
                        exact
                        to={child.link}
                        onClick={() => onClose()}
                        style={{ paddingLeft: 56 }}
                      >
                        <DrawerDot />
                        {child.label}
                      </DrawerItem>
                    ))}
                </div>
              );
            }

            // Stripe
            if (item.type === 'stripe') {
              if (item.shown === false) {
                return (
                  <DrawerItemButton
                    key="stripe"
                    onClick={() => {
                      onPermissionDenied();
                      onClose();
                    }}
                  >
                    <DrawerDot />
                    {item.label}
                  </DrawerItemButton>
                );
              }
              return (
                <DrawerItemButton
                  key="stripe"
                  onClick={() => {
                    onStripeClick();
                    onClose();
                  }}
                  style={{ color: stripeEnabledColor }}
                >
                  <DrawerDot />
                  {item.label}
                </DrawerItemButton>
              );
            }

            // External link
            if (item.type === 'external') {
              return (
                <DrawerItemExternal
                  key={item.link}
                  href={item.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => onClose()}
                >
                  <DrawerDot />
                  {item.label}
                </DrawerItemExternal>
              );
            }

            // Hidden item
            if (item.shown === false) {
              return (
                <DrawerItemButton
                  key={item.link || idx}
                  onClick={() => {
                    onPermissionDenied();
                    onClose();
                  }}
                >
                  <DrawerDot />
                  {item.label}
                </DrawerItemButton>
              );
            }

            // Normal nav item
            return (
              <DrawerItem
                key={item.link}
                exact
                to={item.link}
                onClick={() => onClose()}
              >
                <DrawerDot />
                {item.label}
              </DrawerItem>
            );
          })}
        </DrawerSubGroup>
      )}
    </DrawerSection>
  );
};

export default MobileMenuSection;
